import { Map as IMap } from 'immutable'

/**
 * The machinery behind the entity indexes. Knows about entity maps and index math, and nothing
 * about the store - no `AppStateType`, no action types, no state key. `indexes.ts` declares which
 * indexes exist and wires them in; everything here is reusable and testable on its own.
 *
 * Ids rather than entity references, so an index never holds an identity that something
 * downstream would have to deep-compare. Plain JS rather than Immutable, because the buckets are
 * looked up by reference and never structurally compared, and Immutable's hashing is a
 * measurable share of the profile this work exists to reduce.
 *
 * The price of plain JS is that every write must be copy-on-write: reselect and useSelector
 * detect change by reference, so a set mutated in place is invisible to them and surfaces as a
 * stale row. Only the buckets an action actually touches get a new identity, the index keeps its
 * identity when no bucket moved, and the whole registry keeps its identity when no index moved.
 */

/** What an index groups by. */
export type EntityIndexKey = number | string

export type EntityIndex<Key extends EntityIndexKey = EntityIndexKey, Id extends number = number> = ReadonlyMap<
  Key,
  ReadonlySet<Id>
>

type EntityMap = IMap<number, IMap<string, unknown>>
export type EntitiesState = IMap<string, EntityMap> | undefined

interface EntityIndexDefinition<Entity, Key extends EntityIndexKey> {
  readonly entityType: string
  readonly bucketOf: (entity: Entity) => Key | null | undefined
}

/**
 * One index: the entity type it reads, and which bucket an entity belongs in. Returning null or
 * undefined leaves the entity out - a consignment carries no slot until it is dispatched.
 *
 * Annotate the callback's parameter rather than passing type arguments. TypeScript has no partial
 * inference, so naming the entity type would force you to name the key type as well, while
 * `(c: Consignment) => c.get('slotId')` infers both.
 */
export const createIndex = <Entity, Key extends EntityIndexKey>(
  entityType: string,
  bucketOf: (entity: Entity) => Key | null | undefined
): EntityIndexDefinition<Entity, Key> => ({ entityType, bucketOf })

// The entity type each definition reads is checked at its own call site; the registry holds them
// together and only ever passes an entity back to the definition it came from.
type AnyIndexDefinition = EntityIndexDefinition<never, EntityIndexKey>

type EntityIndexDefinitions = Record<string, AnyIndexDefinition>

type EntityIndexesOf<Definitions extends EntityIndexDefinitions> = {
  readonly [Name in keyof Definitions]: Definitions[Name] extends EntityIndexDefinition<never, infer Key>
    ? EntityIndex<Key>
    : never
}

export const draftEntityIndex = <Key extends EntityIndexKey>(index: EntityIndex<Key>) => {
  let next: Map<Key, ReadonlySet<number>> | undefined
  // The buckets this draft has replaced with a copy of its own. Doubles as the lookup for them,
  // so `own` does not go back to `next`, and keeps `next` honest: the buckets still shared with
  // `index` are only ever read.
  const owned = new Map<Key, Set<number>>()

  const own = (bucket: Key): Set<number> => {
    next ??= new Map(index)
    let mine = owned.get(bucket)
    if (!mine) {
      mine = new Set(next.get(bucket))
      owned.set(bucket, mine)
      next.set(bucket, mine)
    }
    return mine
  }

  const peek = (bucket: Key) => (next ?? index).get(bucket)

  return {
    add: (bucket: Key, id: number) => {
      if (peek(bucket)?.has(id)) return
      own(bucket).add(id)
    },
    remove: (bucket: Key, id: number) => {
      if (!peek(bucket)?.has(id)) return
      own(bucket).delete(id)
    },
    result: (): EntityIndex<Key> => {
      if (!next) return index
      for (const [bucket, ids] of owned) {
        if (ids.size === 0) next.delete(bucket)
      }
      return next
    }
  }
}

const emptyIndex: EntityIndex = new Map()

const bucketOf = (
  definition: AnyIndexDefinition,
  entity: IMap<string, unknown> | undefined
): EntityIndexKey | undefined => {
  if (!entity) return undefined
  const bucket = definition.bucketOf(entity as never)
  return bucket ?? undefined
}

const buildIndex = (definition: AnyIndexDefinition, entities: EntitiesState): EntityIndex => {
  const draft = draftEntityIndex(emptyIndex)
  entities?.get(definition.entityType)?.forEach((entity, id) => {
    const bucket = bucketOf(definition, entity)
    if (bucket !== undefined) draft.add(bucket, Number(id))
  })
  return draft.result()
}

/**
 * One index, brought in step with what the entities reducer just wrote, by reading the bucket of
 * each touched id before and after. Replicating `replaceEntity`'s decision here instead would let
 * the index drift from the store whenever that decision changed.
 *
 * Returns the index it was handed when nothing moved. That is what lets the registry leave itself
 * its identity, so a write no index cares about costs nothing downstream.
 *
 * Gated on this index's own entity type, because most writes leave it alone: clearing production
 * time windows moves `entities` but not `consignments`, and rebuilding for it would hand every
 * route a new set and invalidate every selector reading one.
 */
const updateIndex = (
  definition: AnyIndexDefinition,
  index: EntityIndex,
  touched: Record<string, unknown> | undefined,
  previousEntities: EntitiesState,
  nextEntities: EntitiesState
): EntityIndex => {
  const before = previousEntities?.get(definition.entityType)
  const after = nextEntities?.get(definition.entityType)
  if (before === after) return index

  // Anything the caller cannot enumerate - CLEAR_ENTITIES, logout, a branch added to the entities
  // reducer and not to `payloadOf` - rebuilds, so drift costs time rather than correctness.
  if (!touched) return buildIndex(definition, nextEntities)

  const draft = draftEntityIndex(index)
  for (const entityId of Object.keys(touched)) {
    const id = Number.parseInt(entityId, 10)
    const from = bucketOf(definition, before?.get(id))
    const to = bucketOf(definition, after?.get(id))
    if (from === to) continue
    if (from !== undefined) draft.remove(from, id)
    if (to !== undefined) draft.add(to, id)
  }
  return draft.result()
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined

/**
 * Binds a set of definitions into the three operations the store needs. The names are captured
 * once here rather than derived on every dispatch.
 */
export const createIndexes = <Definitions extends EntityIndexDefinitions>(definitions: Definitions) => {
  type Indexes = EntityIndexesOf<Definitions>
  type MutableIndexes = { -readonly [Name in keyof Indexes]: Indexes[Name] }

  const names = Object.keys(definitions) as (keyof Definitions & string)[]

  const empty = Object.fromEntries(names.map((name) => [name, emptyIndex])) as Indexes

  const build = (entities: EntitiesState): Indexes =>
    Object.fromEntries(names.map((name) => [name, buildIndex(definitions[name], entities)])) as Indexes

  /**
   * `payload` is what the action wrote, keyed by entity type, or undefined when the caller cannot
   * say - see `updateIndex`. The result is allocated only once an index has actually moved, so a
   * write no index cares about leaves the whole registry its identity.
   */
  const update = (
    indexes: Indexes,
    previousEntities: EntitiesState,
    nextEntities: EntitiesState,
    payload: Record<string, unknown> | undefined
  ): Indexes => {
    if (previousEntities === nextEntities) return indexes

    let next: MutableIndexes | undefined
    for (const name of names) {
      const definition = definitions[name]
      const previous = indexes[name]
      const updated = updateIndex(
        definition,
        previous,
        payload && asRecord(payload[definition.entityType]),
        previousEntities,
        nextEntities
      )
      if (updated === previous) continue
      next ??= { ...indexes }
      next[name] = updated as Indexes[typeof name]
    }

    return next ?? indexes
  }

  return { empty, build, update }
}
